/**
 * @file KanbanCard.tsx
 * @description Карточка сделки на доске (phase09; phase19.31 — минимум:
 * «₽» вместо «RUB», срок «12 дн» вместо «12д», шрифт не мельче 12px).
 *
 * `useDraggable` из @dnd-kit/core (сортировка между колонками, не внутри).
 * Цветной полосы слева нет: цвет стадии магазина — точкой в шапке колонки
 * (SC28). Срок: просрочка — янтарной пометкой «▲ 12 дн», срок впереди —
 * серой «3 дн», срока нет — ничего (вместо прежней «ОК»).
 */

import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Clock } from "lucide-react";
import type { CSSProperties } from "react";

import { OverdueMark } from "./deals/DealDeadline";
import { type DealDto } from "../../lib/api-crm";
import { dealDeadline, formatRub } from "../../lib/crm-deal-view";
import { cn } from "@/lib/utils";

interface Props {
  deal: DealDto;
  /** Если true — это рендер в DragOverlay (без useDraggable). */
  dragging?: boolean;
  onClick?: () => void;
}

export function KanbanCard({ deal, dragging, onClick }: Props) {
  const draggable = useDraggable({
    id: deal.id,
    data: { type: "deal", dealId: deal.id, fromStageId: deal.currentStageId },
    disabled: dragging,
  });

  const style: CSSProperties = dragging
    ? { cursor: "grabbing" }
    : { transform: CSS.Translate.toString(draggable.transform), opacity: draggable.isDragging ? 0 : 1 };
  const deadline = dealDeadline(deal);

  return (
    <div
      ref={dragging ? undefined : draggable.setNodeRef}
      style={style}
      {...(dragging ? {} : draggable.attributes)}
      {...(dragging ? {} : draggable.listeners)}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      className={cn(
        "overflow-hidden rounded-lg bg-surface-lowest p-3 shadow-sm cursor-grab active:cursor-grabbing select-none transition-shadow hover:shadow",
        dragging && "rotate-2 shadow-lg",
      )}
    >
      {/* Название сделки — до двух строк (QA-5 D4), полное — в подсказке. */}
      <div className="type-body line-clamp-2 break-words font-medium" title={deal.displayName}>
        {deal.displayName}
      </div>
      {deal.customerDisplayName ? (
        <div className="type-caption truncate text-on-surface-variant">{deal.customerDisplayName}</div>
      ) : null}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="type-body whitespace-nowrap font-semibold tabular-nums">
          {formatRub(deal.expectedAmountGross, "whole")}
        </span>
        {deadline.kind === "overdue" ? (
          <span className="type-caption" title={deadline.until}>
            <OverdueMark text={deadline.overdue} />
          </span>
        ) : deadline.kind === "due" ? (
          <span className="type-caption inline-flex items-center gap-1 tabular-nums text-on-surface-variant" title={deadline.until}>
            <Clock aria-hidden className="size-3.5" />
            {deadline.left}
          </span>
        ) : null}
      </div>
      {deal.ownerDisplayName ? (
        <div className="type-caption mt-1 truncate text-on-surface-variant">{deal.ownerDisplayName}</div>
      ) : null}
    </div>
  );
}
